import {
  Bell,
  CalendarDays,
  LayoutDashboard,
  LogOut,
  Menu,
  QrCode,
  ReceiptText,
  Search,
  ShieldCheck,
  MonitorPlay,
  Tags,
  UserCheck,
  Users,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAdminAuth } from "../../context/AdminAuthContext";
import type { AdminPermission } from "../../types/api";

const navItems: Array<{
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  permission?: AdminPermission;
  roles?: string[];
}> = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/screenings", label: "Screenings", icon: CalendarDays, permission: "events:manage" },
  { to: "/admin/orders", label: "Orders", icon: ReceiptText, permission: "orders:view" },
  {
    to: "/admin/scanner",
    label: "Check-in",
    icon: QrCode,
    permission: "tickets:scan",
  },
  { to: "/admin/users", label: "Users", icon: Users, permission: "users:view" },
  { to: "/admin/catalog", label: "Venues & categories", icon: Tags, permission: "catalog:manage" },
  { to: "/admin/site-content", label: "Site content", icon: MonitorPlay, permission: "site:manage" },
  {
    to: "/admin/exhibitor-applications",
    label: "Exhibitor applications",
    icon: UserCheck,
    permission: "organizers:review",
  },
  { to: "/admin/team", label: "Team", icon: ShieldCheck, permission: "team:manage" },
];

const pageTitles: Record<string, { title: string; subtitle: string }> = {
  "/admin": {
    title: "Dashboard",
    subtitle: "Live revenue, sales velocity, and recent activity.",
  },
  "/admin/screenings": {
    title: "Screenings",
    subtitle: "Create screenings, tune ticket tiers, and manage inventory.",
  },
  "/admin/orders": {
    title: "Orders",
    subtitle: "Track payments, attendees, and check-in progress.",
  },
  "/admin/scanner": {
    title: "Check-in",
    subtitle: "Validate QR tickets at the gate from camera or manual entry.",
  },
  "/admin/users": {
    title: "Users",
    subtitle: "Customers who've signed up and their order activity.",
  },
  "/admin/catalog": {
    title: "Venues & categories",
    subtitle: "Manage reusable screening locations and customer-facing categories.",
  },
  "/admin/site-content": {
    title: "Site content",
    subtitle: "Upload public website media and manage homepage news.",
  },
  "/admin/team": {
    title: "Team",
    subtitle: "Invite admins and control what each team member can do.",
  },
  "/admin/exhibitor-applications": {
    title: "Exhibitor applications",
    subtitle: "Review, approve, or reject exhibitor sign-up requests.",
  },
};

function resolvePageTitle(pathname: string) {
  if (pageTitles[pathname]) return pageTitles[pathname];
  if (pathname.startsWith("/admin/screenings/")) {
    return {
      title: "Screening details",
      subtitle: "Sales, inventory, and attendee activity for this screening.",
    };
  }
  return pageTitles["/admin"];
}

function initials(name?: string) {
  if (!name) return "AD";
  return name
    .split(" ")
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export default function AdminLayout() {
  const { admin, signOut } = useAdminAuth();
  const location = useLocation();
  const header = resolvePageTitle(location.pathname);
  const [mobileOpen, setMobileOpen] = useState(false);

  const visibleNavItems = useMemo(() => {
    const effective = new Set<string>(admin?.effectivePermissions ?? []);
    const isSuperAdmin = admin?.role === "super-admin";
    return navItems.filter((item) => {
      if (item.roles && !item.roles.includes(admin?.role ?? "")) return false;
      if (!item.permission) return true;
      if (isSuperAdmin) return true;
      return effective.has(item.permission);
    });
  }, [admin?.effectivePermissions, admin?.role]);

  const sidebar = (
    <aside className="relative flex h-full w-[268px] shrink-0 flex-col overflow-hidden border-r border-white/15 bg-black">
      <div className="absolute inset-x-0 top-0 h-1 bg-aan-green" />

      <div className="relative px-6 pb-7 pt-8">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[8px] border border-white/15 bg-white/5 p-2">
            <img src="/assets/logo-compact.png" alt="African Art Network" className="max-h-full max-w-full object-contain" />
          </div>
          <div className="min-w-0">
            <div className="truncate font-display text-[17px] font-black uppercase leading-none text-white">
              AAN Admin
            </div>
            <div className="mt-1 text-[11px] font-bold uppercase tracking-[0.18em] text-aan-bright">
              Box office
            </div>
          </div>
        </div>
      </div>

      <nav className="relative flex-1 space-y-1 px-3">
        {visibleNavItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              end={item.to === "/admin"}
              to={item.to}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-[8px] border px-3 py-3 text-sm font-semibold transition ${
                  isActive
                    ? "border-aan-green bg-aan-green text-black"
                    : "border-transparent text-white/68 hover:border-white/15 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon size={18} strokeWidth={isActive ? 2.4 : 2} />
                  <span>{item.label}</span>
                  {isActive && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-black" />}
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="relative border-t border-white/15 p-3">
        <div className="flex items-center gap-3 rounded-[8px] border border-white/10 bg-white/[0.03] px-3 py-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-aan-green text-xs font-black text-black">
            {initials(admin?.name)}
          </div>
          <div className="min-w-0 flex-1 leading-tight">
            <div className="truncate text-sm font-semibold text-white">{admin?.name ?? "Admin"}</div>
            <div className="truncate text-xs text-white/[0.58]">{admin?.email}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={signOut}
          className="mt-2 flex w-full items-center gap-2 rounded-full px-3 py-2.5 text-sm font-bold uppercase text-white/[0.62] transition hover:bg-white/[0.08] hover:text-aan-bright"
        >
          <LogOut size={16} />
          Sign out
        </button>
      </div>
    </aside>
  );

  return (
    <div className="ego-shell relative flex min-h-screen overflow-hidden">
      <div className="relative z-10 hidden lg:block">{sidebar}</div>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute inset-y-0 left-0">{sidebar}</div>
        </div>
      )}

      <div className="relative z-10 flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 border-b border-white/15 bg-black/[0.88] backdrop-blur">
          <div className="flex items-center gap-4 px-4 py-4 sm:px-6 lg:px-8">
            <button
              type="button"
              onClick={() => setMobileOpen((value) => !value)}
              className="ego-btn-ghost lg:hidden"
              aria-label="Toggle navigation"
            >
              {mobileOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
            <img src="/assets/logo-compact.png" alt="African Art Network" className="h-9 w-9 shrink-0 object-contain lg:hidden" />

            <div className="min-w-0 flex-1">
              <div className="font-display text-[11px] font-black uppercase tracking-[0.22em] text-aan-bright">
                African Art Network
              </div>
              <h1 className="truncate font-display text-2xl font-black uppercase leading-none text-white sm:text-3xl">
                {header.title}
              </h1>
              <p className="mt-1 truncate text-sm text-white/[0.62]">{header.subtitle}</p>
            </div>

            <div className="hidden items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-2 text-sm text-white/[0.62] md:flex">
              <Search size={15} />
              <span className="hidden lg:inline">Search</span>
              <kbd className="ml-2 rounded-md border border-white/15 bg-black px-1.5 py-0.5 text-[10px] font-semibold text-white/60">
                ⌘K
              </kbd>
            </div>

            <button
              type="button"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white transition hover:border-aan-green hover:text-aan-bright"
              aria-label="Notifications"
            >
              <Bell size={16} />
            </button>

            <div className="hidden h-10 w-10 items-center justify-center rounded-full bg-aan-green text-xs font-black text-black sm:flex">
              {initials(admin?.name)}
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <div className="mx-auto w-full max-w-[1440px]">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
